//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import * as universalImageActionsApi from '@apis/universal-image-actions/universalImageActionsApi';
import {
  BeautifiedMediaCb,
  IBeautifyImageInfo,
  IBeautifyModalState,
} from '@beautify-modal/redux/beautifyModalModel';
import { BeautifyConfig } from '@legacy-components/common/photo-editor-beautify/beautifyConfig';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import {
  IToastNotification,
  ToastType,
} from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import Cuid from '@models/Cuid';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';

const initialState: IBeautifyModalState = {
  flags: {
    isSavingImage: false,
  },
  errors: {},
};

const beautifyImageFailedToast: IToastNotification = {
  title: 'Failed to Edit Media',
  message: 'We could not save the edits made to your media. Please try again.',
  options: {
    toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
  },
  type: ToastType.ERROR,
};

const { actions, reducer } = createSlice({
  name: SliceNames.BeautifyModal,
  initialState,
  reducers: {
    refreshBeautifiedImageFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isSavingImage = false;
      state.errors.refreshImageError = action.payload;
    },
    requestSaveBeautifyImage(state) {
      state.flags.isSavingImage = true;
    },
    saveBeautifyImageSucceeded(state) {
      state.flags.isSavingImage = false;
    },
    saveBeautifyImageFailed(state, action: PayloadAction<string | undefined>) {
      state.flags.isSavingImage = false;
      state.errors.saveImageError = action.payload;
    },
    setBeautifyModalConfig(state, action: PayloadAction<BeautifyConfig>) {
      state.beautifyConfig = action.payload;
    },
    setBeautifyModalImageInfo(
      state,
      action: PayloadAction<IBeautifyImageInfo>,
    ) {
      state.contentType = action.payload.contentType;
      state.cuid = action.payload.cuid;
      state.fileId = action.payload.fileId;
    },
    resetBeautifyModal() {
      return initialState;
    },
  },
});

export const {
  refreshBeautifiedImageFailed,
  requestSaveBeautifyImage,
  resetBeautifyModal,
  saveBeautifyImageFailed,
  saveBeautifyImageSucceeded,
  setBeautifyModalConfig,
  setBeautifyModalImageInfo,
} = actions;

export const saveBeautifiedImage = createThunk(
  (newImageUrl: string, refreshBeautifiedMedia: BeautifiedMediaCb) =>
    async (dispatch, getState) => {
      const { cuid } = getState().beautifyModal;

      if (cuid && newImageUrl) {
        let beautifyError = false;

        dispatch(requestSaveBeautifyImage());

        const itemId = Cuid.toPartial(cuid);
        const req = {
          imageUrl: newImageUrl,
        };

        try {
          await universalImageActionsApi.beautifyImage(itemId, req);
        } catch (_e) {
          beautifyError = true;
          dispatch(
            saveBeautifyImageFailed(
              'An error occurred while trying to save the beautified image. Please try again or contact Bazaarvoice Support.',
            ),
          );
          dispatch(
            toastNotificationsActions.addToastNotification(
              beautifyImageFailedToast,
            ),
          );
        }

        // Only attempt to refresh the beautified image if beautify call succeeded
        if (!beautifyError) {
          try {
            await refreshBeautifiedMedia(cuid);

            // Only flip `isSavingImage` to false after successfully saving and refreshing
            // the beautified media so that the spinner on the preview media depending on
            // that flag shows thru both API calls
            dispatch(saveBeautifyImageSucceeded());
          } catch (_e) {
            dispatch(
              refreshBeautifiedImageFailed(
                'An error occurred while trying to refresh the beautified image. Please try again or contact Bazaarvoice Support.',
              ),
            );
          }
        }
      }
    },
);

export default reducer;
